/**
 * models
 */
import { BadgeModel } from '@sharedDirectives/badge';
import { IUploadItemModel, UploadItemModel } from '../upload-item-model';

/**
 * interfaces
 */
import { IUploadBatchModel } from './interfaces';
import { IUploadBatchDto } from '../../interfaces';

/**
 * consts
 */
import { createUploadBatchStatusBadge } from '../../consts';

/**
 * One upload batch, ready to render.
 *
 * The server sends its dates as RFC 1123 strings in GMT, which are
 * parsed here once so the template only formats them. A date that fails
 * to parse comes out as null rather than as an "Invalid Date".
 */
export class UploadBatchModel implements IUploadBatchModel {

    /** Built once on first read, since the items never change. */
    private _items: IUploadItemModel[] | null = null;

    /** Built once on first read, since the badge never changes. */
    private _statusBadge: BadgeModel<boolean> | null = null;

    /**
     * @param dto - The raw batch as it came off the wire.
     */
    constructor(public readonly dto: IUploadBatchDto) {}

    /** @inheritdoc */
    public get batchId(): string {
        return this.dto?.batch_id ?? '';
    }

    /** @inheritdoc */
    public get batchType(): string {
        return this.dto?.batch_type ?? '';
    }

    /** @inheritdoc */
    public get totalFiles(): number {
        return this.dto?.details?.total_files ?? 0;
    }

    /** @inheritdoc */
    public get processingCompleted(): boolean {
        return this.dto?.processing_completed === true;
    }

    /** @inheritdoc */
    public get uploadedAt(): Date | null {
        return UploadBatchModel._parseDate(this.dto?.uploaded_at);
    }

    /** @inheritdoc */
    public get completedAt(): Date | null {
        return UploadBatchModel._parseDate(this.dto?.processing_completed_at);
    }

    /** @inheritdoc */
    public get items(): IUploadItemModel[] {
        this._items ??= (this.dto?.upload_items ?? []).map((item) => new UploadItemModel(item));
        return this._items;
    }

    /** @inheritdoc */
    public statusBadge(): BadgeModel<boolean> {
        this._statusBadge ??= createUploadBatchStatusBadge(this.processingCompleted);
        return this._statusBadge;
    }

    /**
     * Turns a wire date into a Date, or null when it is missing or not a
     * date the browser understands.
     *
     * @param raw - The date string sent by the server.
     * @returns The parsed date, or null.
     */
    private static _parseDate(raw: string | null | undefined): Date | null {

        if (raw == null || raw.length === 0) return null;

        const parsed = new Date(raw);

        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

}
